import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { 
  Package, 
  Plus, 
  ShoppingCart, 
  Minus, 
  AlertTriangle, 
  TrendingUp, 
  FileText,
  LogOut,
  Building,
  BarChart3,
  Calendar,
  Mail,
  PieChart
} from 'lucide-react';
import AnalyticsDashboard from './AnalyticsDashboard';

function ProDashboard() {
  const { user, logout } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('stock');
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [stockStatus, setStockStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showMovement, setShowMovement] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [showEditItem, setShowEditItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [newItem, setNewItem] = useState({
    nom: '',
    categorie: '',
    unite: 'kg',
    seuil_alerte: '',
    perissable: false,
    dluo_dlc: ''
  });
  
  const [newMovement, setNewMovement] = useState({
    item_id: '',
    type: 'ACHAT',
    quantite: '',
    source: '',
    note: ''
  });

  const [paywallActive, setPaywallActive] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsRes, movementsRes, stockRes] = await Promise.all([
        axios.get('/items'),
        axios.get('/movements'),
        axios.get('/stock')
      ]);
      
      setItems(itemsRes.data);
      setMovements(movementsRes.data);
      setStockStatus(stockRes.data);
      setPaywallActive(false);
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail?.startsWith('PAYWALL:')) {
        setPaywallActive(true);
        toast.error(error.response.data.detail.replace('PAYWALL:', ''));
      } else {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      const itemData = {
        ...newItem,
        secteur: 'PRO',
        seuil_alerte: parseFloat(newItem.seuil_alerte),
        dluo_dlc: newItem.dluo_dlc || null
      };
      
      await axios.post('/items', itemData);
      toast.success('Article ajouté avec succès');
      setNewItem({
        nom: '',
        categorie: '',
        unite: 'kg',
        seuil_alerte: '',
        perissable: false,
        dluo_dlc: ''
      });
      setShowAddItem(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout de l\'article');
    }
  };

  const handleAddMovement = async (e) => {
    e.preventDefault();
    try {
      const movementData = {
        ...newMovement,
        quantite: parseFloat(newMovement.quantite)
      };
      
      await axios.post('/movements', movementData);
      toast.success('Mouvement enregistré avec succès');
      setNewMovement({
        item_id: '',
        type: 'ACHAT',
        quantite: '',
        source: '',
        note: ''
      });
      setShowMovement(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement du mouvement');
    }
  };

  const quickMovement = async (itemId, type, quantity = 1) => {
    try {
      await axios.post('/movements', {
        item_id: itemId,
        type: type,
        quantite: quantity,
        source: type === 'ACHAT' ? 'Ajout rapide' : 'Vente rapide'
      });
      toast.success(`${type === 'ACHAT' ? 'Achat' : 'Vente'} enregistré`);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const sendDailyReport = async () => {
    try {
      const response = await axios.post('/reports/send-daily');
      if (response.data.error) {
        toast.error(response.data.message);
      } else {
        toast.success(`Rapport envoyé à ${response.data.details?.email || 'votre email'} (${response.data.details?.total_articles || 0} articles)`);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du rapport');
    }
  };

  const testEmail = async () => {
    try {
      const response = await axios.post('/test-email');
      if (response.data.success) {
        toast.success(`Email de test envoyé à ${response.data.email}`);
      } else {
        toast.warning(response.data.message + (response.data.note ? ` - ${response.data.note}` : ''));
      }
    } catch (error) {
      toast.error('Erreur lors du test d\'email');
    }
  };

  const handleEditItem = (item) => {
    setEditingItem({...item});
    setShowEditItem(true);
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/items/${editingItem.id}`, {
        nom: editingItem.nom,
        categorie: editingItem.categorie,
        unite: editingItem.unite,
        secteur: editingItem.secteur,
        seuil_alerte: parseFloat(editingItem.seuil_alerte),
        perissable: editingItem.perissable,
        dluo_dlc: editingItem.dluo_dlc || null
      });
      
      toast.success('Article mis à jour avec succès');
      setShowEditItem(false);
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const alertsCount = stockStatus.filter(item => item.en_alerte).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-cyan-50 to-teal-100">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          <p className="text-emerald-700 font-medium">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-cyan-50 to-teal-100">
      {/* Navigation */}
      <nav className="glass-nav sticky top-0 z-50 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center space-x-4">
            <div className="bg-gradient-primary p-2 rounded-lg">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">InventoTrack Pro</h1>
              <p className="text-sm text-gray-600">{user?.username}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {alertsCount > 0 && (
              <Badge variant="destructive" className="animate-pulse" data-testid="alerts-badge">
                {alertsCount} alerte{alertsCount > 1 ? 's' : ''}
              </Badge>
            )}
            <Button 
              variant="outline" 
              onClick={logout}
              className="flex items-center space-x-2"
              data-testid="logout-button"
            >
              <LogOut size={16} />
              <span>Déconnexion</span>
            </Button>
          </div>
        </div>
      </nav>

      <div className="container-responsive py-8">
        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="card hover-lift" data-testid="total-items-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Articles totaux</p>
                  <p className="text-2xl font-bold text-gray-900">{items.length}</p>
                </div>
                <Package className="w-8 h-8 text-emerald-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="card hover-lift" data-testid="alerts-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Alertes stock</p>
                  <p className="text-2xl font-bold text-red-600">{alertsCount}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="card hover-lift" data-testid="movements-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Mouvements (7j)</p>
                  <p className="text-2xl font-bold text-blue-600">{movements.length}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="card hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">Rapport journalier</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    <Button 
                      size="sm" 
                      onClick={sendDailyReport}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2 py-1"
                      data-testid="send-report-button"
                    >
                      📊 Envoyer
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={testEmail}
                      className="text-xs px-2 py-1 border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                      data-testid="test-email-button"
                    >
                      🧪 Test
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => alert('Configuration email disponible dans les paramètres')}
                      className="text-xs px-2 py-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                      data-testid="config-email-button-pro"
                    >
                      ⚙️ Config
                    </Button>
                  </div>
                </div>
                <Mail className="w-8 h-8 text-emerald-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Onglets principaux */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex h-auto w-full flex-wrap items-stretch justify-start gap-2" data-testid="main-tabs">
            <TabsTrigger value="stock" className="min-w-[120px] flex-1">Stock</TabsTrigger>
            <TabsTrigger value="articles" className="min-w-[120px] flex-1">Articles</TabsTrigger>
            <TabsTrigger value="mouvements" className="min-w-[120px] flex-1">Mouvements</TabsTrigger>
            <TabsTrigger value="analytics" className="min-w-[120px] flex-1" data-testid="analytics-tab">Analytics</TabsTrigger>
            <TabsTrigger value="rapports" className="min-w-[120px] flex-1">Rapports</TabsTrigger>
          </TabsList>
          
          {/* Onglet Stock */}
          <TabsContent value="stock" className="mt-6">
            <div className="grid gap-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">État des stocks</h2>
                <div className="space-x-2">
                  <Button
                    onClick={() => setShowMovement(true)}
                    className="btn-modern btn-secondary"
                    data-testid="add-movement-button"
                  >
                    <Plus size={16} className="mr-2" />
                    Ajouter mouvement
                  </Button>
                </div>
              </div>
              
              <div className="grid gap-4">
                {stockStatus.map((stock) => (
                  <Card 
                    key={stock.item_id} 
                    className={`card ${stock.en_alerte ? 'border-red-300 bg-red-50' : ''}`}
                    data-testid={`stock-item-${stock.item_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{stock.item_nom}</h3>
                            <div className="flex items-center space-x-4 mt-1">
                              <span className="text-sm text-gray-600">
                                Stock: <span className="font-medium">{stock.stock_actuel} {stock.unite}</span>
                              </span>
                              {stock.jours_restants && (
                                <span className="text-sm text-gray-600">
                                  Reste: <span className="font-medium">{stock.jours_restants.toFixed(1)} jours</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {stock.en_alerte && (
                            <Badge variant="destructive">Alerte</Badge>
                          )}
                          <Button
                            size="sm"
                            onClick={() => quickMovement(stock.item_id, 'ACHAT')}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`quick-buy-${stock.item_id}`}
                          >
                            <ShoppingCart size={16} />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => quickMovement(stock.item_id, 'VENTE')}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid={`quick-sell-${stock.item_id}`}
                          >
                            <Minus size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>
          
          {/* Onglet Articles */}
          <TabsContent value="articles" className="mt-6">
            <div className="grid gap-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Gestion des articles</h2>
                <Button
                  onClick={() => setShowAddItem(true)}
                  className="btn-modern btn-primary"
                  data-testid="add-item-button"
                >
                  <Plus size={16} className="mr-2" />
                  Nouvel article
                </Button>
              </div>
              
              <div className="grid gap-4">
                {items.map((item) => (
                  <Card 
                    key={item.id} 
                    className="card card-interactive cursor-pointer hover:border-emerald-300" 
                    onClick={() => handleEditItem(item)}
                    data-testid={`item-${item.id}`}
                    title="Cliquez pour modifier le seuil d'alarme"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{item.nom}</h3>
                          <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                            <span>Catégorie: {item.categorie}</span>
                            <span>Unité: {item.unite}</span>
                            <span className="font-medium text-emerald-600">Seuil: {item.seuil_alerte} {item.unite}</span>
                            {item.perissable && <Badge variant="secondary">Périssable</Badge>}
                          </div>
                          <p className="text-xs text-emerald-500 mt-1">✏️ Cliquez pour modifier le seuil d'alarme</p>
                        </div>
                        <div className="text-emerald-600">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>
          
          {/* Onglet Mouvements */}
          <TabsContent value="mouvements" className="mt-6">
            <div className="grid gap-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Historique des mouvements</h2>
              </div>
              
              <div className="grid gap-4">
                {movements.slice(0, 20).map((movement) => {
                  const item = items.find(i => i.id === movement.item_id);
                  return (
                    <Card key={movement.id} className="card" data-testid={`movement-${movement.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className={`p-2 rounded-lg ${
                              movement.type === 'ACHAT' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                            }`}>
                              {movement.type === 'ACHAT' ? <ShoppingCart size={16} /> : <Minus size={16} />}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{item?.nom || 'Article supprimé'}</p>
                              <p className="text-sm text-gray-600">
                                {movement.type} - {movement.quantite} {item?.unite}
                              </p>
                            </div>
                          </div>
                          <div className="text-right text-sm text-gray-600">
                            <p>{new Date(movement.date).toLocaleDateString()}</p>
                            <p>{new Date(movement.date).toLocaleTimeString()}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </TabsContent>
          
          {/* Onglet Analytics */}
          <TabsContent value="analytics" className="mt-6">
            <AnalyticsDashboard />
          </TabsContent>

          {/* Onglet Rapports */}
          <TabsContent value="rapports" className="mt-6">
            <div className="grid gap-6">
              <h2 className="text-2xl font-bold text-gray-900">Rapports et analyses</h2>
              
              <div className="grid gap-4">
                <Card className="card card-interactive" onClick={sendDailyReport} data-testid="daily-report-card">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-emerald-100 rounded-lg">
                        <Mail className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Rapport journalier</h3>
                        <p className="text-gray-600">Envoyer le rapport du jour par email</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="card">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <BarChart3 className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Analyses avancées</h3>
                        <p className="text-gray-600">Fonctionnalité à venir dans la prochaine version</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal Ajout Article */}
      {showAddItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md" data-testid="add-item-modal">
            <CardHeader>
              <CardTitle>Nouvel article</CardTitle>
              <CardDescription>Ajouter un nouvel article à votre stock</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddItem} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nom">Nom de l'article</Label>
                  <Input
                    id="nom"
                    value={newItem.nom}
                    onChange={(e) => setNewItem({...newItem, nom: e.target.value})}
                    required
                    data-testid="item-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="categorie">Catégorie</Label>
                  <Input
                    id="categorie"
                    value={newItem.categorie}
                    onChange={(e) => setNewItem({...newItem, categorie: e.target.value})}
                    required
                    data-testid="item-category-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Unité</Label>
                    <Select 
                      value={newItem.unite} 
                      onValueChange={(value) => setNewItem({...newItem, unite: value})}
                    >
                      <SelectTrigger data-testid="item-unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="g">g</SelectItem>
                        <SelectItem value="L">L</SelectItem>
                        <SelectItem value="ml">ml</SelectItem>
                        <SelectItem value="pièces">pièces</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="seuil_alerte">Seuil d'alerte</Label>
                    <Input
                      id="seuil_alerte"
                      type="number"
                      step="0.1"
                      value={newItem.seuil_alerte}
                      onChange={(e) => setNewItem({...newItem, seuil_alerte: e.target.value})}
                      required
                      data-testid="item-threshold-input"
                    />
                  </div>
                </div>
                
                <div className="flex space-x-2 justify-end">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowAddItem(false)}
                    data-testid="cancel-add-item"
                  >
                    Annuler
                  </Button>
                  <Button type="submit" data-testid="submit-add-item">
                    Ajouter
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Mouvement */}
      {showMovement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md" data-testid="add-movement-modal">
            <CardHeader>
              <CardTitle>Nouveau mouvement</CardTitle>
              <CardDescription>Enregistrer un achat ou une vente</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddMovement} className="space-y-4">
                <div className="space-y-2">
                  <Label>Article</Label>
                  <Select 
                    value={newMovement.item_id} 
                    onValueChange={(value) => setNewMovement({...newMovement, item_id: value})}
                  >
                    <SelectTrigger data-testid="movement-item-select">
                      <SelectValue placeholder="Sélectionner un article" />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.nom} ({item.unite})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select 
                      value={newMovement.type} 
                      onValueChange={(value) => setNewMovement({...newMovement, type: value})}
                    >
                      <SelectTrigger data-testid="movement-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACHAT">Achat</SelectItem>
                        <SelectItem value="VENTE">Vente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="quantite">Quantité</Label>
                    <Input
                      id="quantite"
                      type="number"
                      step="0.1"
                      value={newMovement.quantite}
                      onChange={(e) => setNewMovement({...newMovement, quantite: e.target.value})}
                      required
                      data-testid="movement-quantity-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="source">Source (optionnel)</Label>
                  <Input
                    id="source"
                    value={newMovement.source}
                    onChange={(e) => setNewMovement({...newMovement, source: e.target.value})}
                    placeholder="Ex: Fournisseur ABC"
                    data-testid="movement-source-input"
                  />
                </div>
                
                <div className="flex space-x-2 justify-end">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowMovement(false)}
                    data-testid="cancel-add-movement"
                  >
                    Annuler
                  </Button>
                  <Button type="submit" data-testid="submit-add-movement">
                    Enregistrer
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Édition Article Pro */}
      {showEditItem && editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md" data-testid="edit-item-modal-pro">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Package className="w-5 h-5 text-emerald-600" />
                <span>Modifier l'article professionnel</span>
              </CardTitle>
              <CardDescription>
                Ajustez le seuil d'alarme et autres paramètres
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateItem} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_nom_pro">Nom de l'article</Label>
                  <Input
                    id="edit_nom_pro"
                    value={editingItem.nom}
                    onChange={(e) => setEditingItem({...editingItem, nom: e.target.value})}
                    required
                    data-testid="edit-item-name-input-pro"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit_categorie_pro">Catégorie</Label>
                  <Input
                    id="edit_categorie_pro"
                    value={editingItem.categorie}
                    onChange={(e) => setEditingItem({...editingItem, categorie: e.target.value})}
                    required
                    data-testid="edit-item-category-input-pro"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Unité</Label>
                    <Select 
                      value={editingItem.unite} 
                      onValueChange={(value) => setEditingItem({...editingItem, unite: value})}
                    >
                      <SelectTrigger data-testid="edit-item-unit-select-pro">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="g">g</SelectItem>
                        <SelectItem value="L">L</SelectItem>
                        <SelectItem value="ml">ml</SelectItem>
                        <SelectItem value="pièces">pièces</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="edit_seuil_alerte_pro" className="text-emerald-700 font-semibold">
                      ⚠️ Seuil d'alerte
                    </Label>
                    <Input
                      id="edit_seuil_alerte_pro"
                      type="number"
                      step="0.1"
                      value={editingItem.seuil_alerte}
                      onChange={(e) => setEditingItem({...editingItem, seuil_alerte: e.target.value})}
                      required
                      className="border-emerald-300 focus:border-emerald-500"
                      data-testid="edit-item-threshold-input-pro"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit_perissable_pro"
                      checked={editingItem.perissable}
                      onChange={(e) => setEditingItem({...editingItem, perissable: e.target.checked})}
                      className="w-4 h-4 text-emerald-600"
                    />
                    <Label htmlFor="edit_perissable_pro">Article périssable</Label>
                  </div>
                </div>
                
                <div className="bg-emerald-50 p-3 rounded-lg">
                  <p className="text-sm text-emerald-700">
                    💼 <strong>Seuil professionnel :</strong> Définissez le niveau minimal de stock pour éviter les ruptures dans votre activité.
                  </p>
                </div>
                
                <div className="flex space-x-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowEditItem(false);
                      setEditingItem(null);
                    }}
                    className="flex-1"
                    data-testid="cancel-edit-item-pro"
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="submit-edit-item-pro"
                  >
                    Sauvegarder
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default ProDashboard;